/**
 * New-project modal — captures everything the TCEQ Form 20982
 * resolver can't derive from the site, the user record, or the GCD
 * directory.
 *
 * Required (3): subdivision name, lot count, tax parcel.
 * Optional sections (collapsed by default to keep the modal scannable):
 *   - Owner contact (item 3)
 *   - Applicant contact (item 4) with "same as owner" toggle
 *   - PE address + phone (item 5; goes to form_inputs because the
 *     User model has no address column)
 *   - Subdivision details (items 2, 9): previous tract names, land use
 *   - Narrative blocks (items 15, 17a, 17b, 19, 44): advanced
 *
 * Flow on submit:
 *   1. createProject(3 required fields)
 *   2. patchProject(everything else) — only if any optional field is set
 *   3. generateForm(projectId) — when autoGenerate=true (default)
 *
 * Light client-side validation (state = 2-char alpha, zip = 5 or 5-4)
 * keeps the renderer from showing obvious garbage in the PDF.
 */
import { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  TceqProjectCreate,
  TceqProjectPatch,
  tceqApi,
} from '../../api/tceq';

interface TceqNewProjectModalProps {
  siteId?: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  autoGenerate?: boolean;
}

const LAND_USE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'single_family_residential', label: 'Single-family residential' },
  { value: 'mixed_use_residential', label: 'Mixed-use residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'agricultural', label: 'Agricultural' },
  { value: 'other', label: 'Other (explain below)' },
];

const STATE_RX = /^[A-Z]{2}$/;
const ZIP_RX = /^\d{5}(-\d{4})?$/;

function nullish(s: string): string | undefined {
  const trimmed = s.trim();
  return trimmed ? trimmed : undefined;
}

export function TceqNewProjectModal({
  siteId,
  onClose,
  onCreated,
  autoGenerate = true,
}: TceqNewProjectModalProps) {
  // ── Required ──────────────────────────────────────────────────
  const [subdivisionName, setSubdivisionName] = useState('');
  const [lotCount, setLotCount] = useState<string>('');
  const [taxBook, setTaxBook] = useState('');
  const [taxMap, setTaxMap] = useState('');
  const [taxParcel, setTaxParcel] = useState('');

  // ── Subdivision detail (items 2, 9) ───────────────────────────
  const [previousTractNames, setPreviousTractNames] = useState('');
  const [landUsePurpose, setLandUsePurpose] = useState(
    'single_family_residential',
  );
  const [otherExplain, setOtherExplain] = useState('');

  // ── Owner (item 3) ────────────────────────────────────────────
  const [ownerName, setOwnerName] = useState('');
  const [ownerAddress, setOwnerAddress] = useState('');
  const [ownerCity, setOwnerCity] = useState('');
  const [ownerState, setOwnerState] = useState('');
  const [ownerZip, setOwnerZip] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  // ── Applicant (item 4) ────────────────────────────────────────
  const [applicantSameAsOwner, setApplicantSameAsOwner] = useState(true);
  const [applicantName, setApplicantName] = useState('');
  const [applicantAddress, setApplicantAddress] = useState('');
  const [applicantCity, setApplicantCity] = useState('');
  const [applicantState, setApplicantState] = useState('');
  const [applicantZip, setApplicantZip] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');

  // ── PE (item 5; form_inputs.items_5_pe_*) ─────────────────────
  const [peAddress, setPeAddress] = useState('');
  const [pePhone, setPePhone] = useState('');

  // ── Narratives (items 15, 17a, 17b, 19, 44) ───────────────────
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [nonResidentialUseTypes, setNonResidentialUseTypes] = useState('');
  const [nonResidentialDemandList, setNonResidentialDemandList] = useState('');
  const [demandSources, setDemandSources] = useState('');
  const [assumptions, setAssumptions] = useState('');

  // ── UI state ──────────────────────────────────────────────────
  const [showOwner, setShowOwner] = useState(false);
  const [showApplicant, setShowApplicant] = useState(false);
  const [showPE, setShowPE] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showNarratives, setShowNarratives] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusLine, setStatusLine] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (!subdivisionName.trim()) return 'Subdivision name is required';
    if (lotCount.trim()) {
      const n = parseInt(lotCount.trim(), 10);
      if (Number.isNaN(n) || n < 0) {
        return 'Number of proposed lots must be a non-negative integer';
      }
    }
    if (ownerState.trim() && !STATE_RX.test(ownerState.trim().toUpperCase())) {
      return "Owner state must be 2 letters (e.g. 'TX')";
    }
    if (ownerZip.trim() && !ZIP_RX.test(ownerZip.trim())) {
      return "Owner ZIP must be 5 digits (or 5-4, e.g. '79101' or '79101-1234')";
    }
    if (!applicantSameAsOwner) {
      if (
        applicantState.trim()
        && !STATE_RX.test(applicantState.trim().toUpperCase())
      ) {
        return "Applicant state must be 2 letters (e.g. 'TX')";
      }
      if (applicantZip.trim() && !ZIP_RX.test(applicantZip.trim())) {
        return "Applicant ZIP must be 5 digits (or 5-4)";
      }
    }
    if (landUsePurpose === 'other' && !otherExplain.trim()) {
      return "Please explain in 'Other' when land use is 'Other'";
    }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    try {
      // 1) Create with the 3 required fields.
      setStatusLine('Creating project…');
      const createBody: TceqProjectCreate = {
        subdivision_name: subdivisionName.trim(),
        site_id: siteId,
      };
      if (lotCount.trim()) {
        createBody.proposed_lot_count = parseInt(lotCount.trim(), 10);
      }
      if (taxBook.trim()) createBody.tax_book = taxBook.trim();
      if (taxMap.trim()) createBody.tax_map = taxMap.trim();
      if (taxParcel.trim()) createBody.tax_parcel = taxParcel.trim();
      const created = await tceqApi.createProject(createBody);

      // 2) PATCH everything else. Only fire if anything optional is set.
      const owner = {
        owner_name: nullish(ownerName),
        owner_address_line1: nullish(ownerAddress),
        owner_city: nullish(ownerCity),
        owner_state: nullish(ownerState.toUpperCase()),
        owner_zip: nullish(ownerZip),
        owner_phone: nullish(ownerPhone),
        owner_email: nullish(ownerEmail),
      };
      const applicant = applicantSameAsOwner
        ? {
          applicant_name: owner.owner_name,
          applicant_address_line1: owner.owner_address_line1,
          applicant_city: owner.owner_city,
          applicant_state: owner.owner_state,
          applicant_zip: owner.owner_zip,
          applicant_phone: owner.owner_phone,
          applicant_email: owner.owner_email,
        }
        : {
          applicant_name: nullish(applicantName),
          applicant_address_line1: nullish(applicantAddress),
          applicant_city: nullish(applicantCity),
          applicant_state: nullish(applicantState.toUpperCase()),
          applicant_zip: nullish(applicantZip),
          applicant_phone: nullish(applicantPhone),
          applicant_email: nullish(applicantEmail),
        };
      const form_inputs: Record<string, unknown> = {};
      const peA = nullish(peAddress);
      const peP = nullish(pePhone);
      if (peA !== undefined) form_inputs.items_5_pe_address = peA;
      if (peP !== undefined) form_inputs.items_5_pe_phone = peP;
      if (nullish(additionalInfo) !== undefined) {
        form_inputs.items_15_additional_info = additionalInfo.trim();
      }
      if (nullish(nonResidentialUseTypes) !== undefined) {
        form_inputs.items_17a_use_types = nonResidentialUseTypes.trim();
      }
      if (nullish(nonResidentialDemandList) !== undefined) {
        form_inputs.items_17b_per_type_demand = nonResidentialDemandList.trim();
      }
      if (nullish(demandSources) !== undefined) {
        form_inputs.items_19_sources = demandSources.trim();
      }
      if (nullish(assumptions) !== undefined) {
        form_inputs.items_44_assumptions = assumptions.trim();
      }
      // Item 13 distribution method — default to individual_wells so
      // item 14 falls out to N/A. Builders override in a future portal.
      form_inputs.items_13_method = 'individual_wells';

      const patch: TceqProjectPatch = {
        version: created.version,
        previous_tract_names: nullish(previousTractNames),
        land_use_purpose: landUsePurpose,
        ...owner,
        ...applicant,
        ...(Object.keys(form_inputs).length > 0 ? { form_inputs } : {}),
      };

      // Always-on patch — at minimum we send the land_use_purpose so the
      // computed:land_use_purpose_display helper gets the right value
      // even if the user accepted the default.
      setStatusLine('Saving project details…');
      await tceqApi.patchProject(created.project_id, patch);

      // 3) Generate.
      if (autoGenerate) {
        setStatusLine('Generating filled PDF…');
        try {
          await tceqApi.generateForm(created.project_id);
        } catch (e: any) {
          setError(
            e?.response?.data?.detail
              || e?.message
              || 'Project saved, but auto-generation failed. '
                 + 'Use the regenerate button in the library to retry.',
          );
        }
      }
      await onCreated();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || 'Failed to create project',
      );
    } finally {
      setSubmitting(false);
      setStatusLine(null);
    }
  }

  // ── UI helpers ─────────────────────────────────────────────────

  function SectionHeader({
    open, setOpen, label, hint,
  }: {
    open: boolean;
    setOpen: (v: boolean) => void;
    label: string;
    hint?: string;
  }) {
    return (
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-mono-900/60 hover:bg-mono-900 border border-mono-800 rounded text-left"
      >
        <span className="flex items-center gap-2">
          {open
            ? <ChevronDownIcon className="w-3.5 h-3.5 text-mono-400" />
            : <ChevronRightIcon className="w-3.5 h-3.5 text-mono-400" />}
          <span className="text-sm text-mono-100">{label}</span>
          {hint && <span className="text-[10px] text-mono-500">{hint}</span>}
        </span>
      </button>
    );
  }

  const inputClass = (
    "w-full px-3 py-2 bg-mono-900 border border-mono-700 rounded text-sm "
    + "text-mono-100 focus:border-cyan-500 focus:outline-none"
  );
  const labelClass = "block text-xs text-mono-300 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-mono-950 border border-mono-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-mono-800 px-5 py-4">
          <h3 className="text-base font-medium text-mono-100">
            New TCEQ Form 20982 Project
          </h3>
          <button
            onClick={onClose}
            className="text-mono-400 hover:text-mono-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-5 py-5 space-y-4 overflow-y-auto"
        >
          <p className="text-xs text-mono-400">
            Subdivision name, lot count, and tax parcel are required.
            Owner, applicant, PE, and narrative sections fill what CARI
            can't derive from the site or your account — open them as
            needed. CARI auto-fills the analytical sections (aquifer,
            drawdowns, GCD info) at Generate time.
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 text-red-300 text-xs">
              {error}
            </div>
          )}

          {statusLine && !error && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-md p-3 text-cyan-200 text-xs flex items-center gap-2">
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10"
                  stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              {statusLine}
            </div>
          )}

          {/* ── Required block ───────────────────────────────────── */}
          <div className="space-y-3">
            <div>
              <label className={labelClass}>
                Subdivision Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={subdivisionName}
                onChange={(e) => setSubdivisionName(e.target.value)}
                autoFocus
                className={inputClass}
                placeholder="e.g. Springbrook Ranch Phase 1"
              />
            </div>

            <div>
              <label className={labelClass}>Number of Proposed Lots</label>
              <input
                type="number" min="0"
                value={lotCount}
                onChange={(e) => setLotCount(e.target.value)}
                className={inputClass}
                placeholder="e.g. 240"
              />
            </div>

            <div>
              <label className={labelClass}>Tax Assessor Parcel</label>
              <div className="grid grid-cols-3 gap-2">
                <input type="text" value={taxBook}
                  onChange={(e) => setTaxBook(e.target.value)}
                  className={inputClass} placeholder="Book" />
                <input type="text" value={taxMap}
                  onChange={(e) => setTaxMap(e.target.value)}
                  className={inputClass} placeholder="Map" />
                <input type="text" value={taxParcel}
                  onChange={(e) => setTaxParcel(e.target.value)}
                  className={inputClass} placeholder="Parcel" />
              </div>
            </div>
          </div>

          {/* ── Subdivision detail ───────────────────────────────── */}
          <div className="space-y-2">
            <SectionHeader
              open={showDetail} setOpen={setShowDetail}
              label="Subdivision detail (items 2, 9)"
              hint="Previous tract names + land use"
            />
            {showDetail && (
              <div className="space-y-3 px-1 pt-2">
                <div>
                  <label className={labelClass}>
                    Previous tract names (item 2)
                  </label>
                  <input type="text" value={previousTractNames}
                    onChange={(e) => setPreviousTractNames(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Originally platted as Section 12 (1882)"
                  />
                </div>
                <div>
                  <label className={labelClass}>Land use (item 9)</label>
                  <select
                    value={landUsePurpose}
                    onChange={(e) => setLandUsePurpose(e.target.value)}
                    className={inputClass}
                  >
                    {LAND_USE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {landUsePurpose === 'other' && (
                  <div>
                    <label className={labelClass}>
                      Explain (required when "Other")
                    </label>
                    <textarea rows={2}
                      value={otherExplain}
                      onChange={(e) => setOtherExplain(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Owner ────────────────────────────────────────────── */}
          <div className="space-y-2">
            <SectionHeader
              open={showOwner} setOpen={setShowOwner}
              label="Property owner (item 3)"
              hint="Name, address, phone, email"
            />
            {showOwner && (
              <div className="space-y-3 px-1 pt-2">
                <input type="text" value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  className={inputClass} placeholder="Owner name (LLC, individual, etc.)" />
                <input type="text" value={ownerAddress}
                  onChange={(e) => setOwnerAddress(e.target.value)}
                  className={inputClass} placeholder="Street address" />
                <div className="grid grid-cols-6 gap-2">
                  <input type="text" value={ownerCity}
                    onChange={(e) => setOwnerCity(e.target.value)}
                    className={`${inputClass} col-span-3`} placeholder="City" />
                  <input type="text" value={ownerState}
                    onChange={(e) => setOwnerState(e.target.value.toUpperCase())}
                    maxLength={2}
                    className={`${inputClass} col-span-1`} placeholder="ST" />
                  <input type="text" value={ownerZip}
                    onChange={(e) => setOwnerZip(e.target.value)}
                    className={`${inputClass} col-span-2`} placeholder="ZIP" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="tel" value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                    className={inputClass} placeholder="Phone" />
                  <input type="email" value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    className={inputClass} placeholder="Email" />
                </div>
              </div>
            )}
          </div>

          {/* ── Applicant ────────────────────────────────────────── */}
          <div className="space-y-2">
            <SectionHeader
              open={showApplicant} setOpen={setShowApplicant}
              label="Plat applicant (item 4)"
              hint={applicantSameAsOwner ? "Same as owner" : "Separate"}
            />
            {showApplicant && (
              <div className="space-y-3 px-1 pt-2">
                <label className="flex items-center gap-2 text-xs text-mono-300">
                  <input type="checkbox"
                    checked={applicantSameAsOwner}
                    onChange={(e) => setApplicantSameAsOwner(e.target.checked)}
                  />
                  Same as owner
                </label>
                {!applicantSameAsOwner && (
                  <>
                    <input type="text" value={applicantName}
                      onChange={(e) => setApplicantName(e.target.value)}
                      className={inputClass} placeholder="Applicant name" />
                    <input type="text" value={applicantAddress}
                      onChange={(e) => setApplicantAddress(e.target.value)}
                      className={inputClass} placeholder="Street address" />
                    <div className="grid grid-cols-6 gap-2">
                      <input type="text" value={applicantCity}
                        onChange={(e) => setApplicantCity(e.target.value)}
                        className={`${inputClass} col-span-3`} placeholder="City" />
                      <input type="text" value={applicantState}
                        onChange={(e) => setApplicantState(e.target.value.toUpperCase())}
                        maxLength={2}
                        className={`${inputClass} col-span-1`} placeholder="ST" />
                      <input type="text" value={applicantZip}
                        onChange={(e) => setApplicantZip(e.target.value)}
                        className={`${inputClass} col-span-2`} placeholder="ZIP" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="tel" value={applicantPhone}
                        onChange={(e) => setApplicantPhone(e.target.value)}
                        className={inputClass} placeholder="Phone" />
                      <input type="email" value={applicantEmail}
                        onChange={(e) => setApplicantEmail(e.target.value)}
                        className={inputClass} placeholder="Email" />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── PE address/phone ─────────────────────────────────── */}
          <div className="space-y-2">
            <SectionHeader
              open={showPE} setOpen={setShowPE}
              label="Professional Engineer (item 5)"
              hint="Address + phone (name + license from your account)"
            />
            {showPE && (
              <div className="space-y-3 px-1 pt-2">
                <input type="text" value={peAddress}
                  onChange={(e) => setPeAddress(e.target.value)}
                  className={inputClass}
                  placeholder="Street address (e.g. 456 PE Lane, Amarillo, TX 79101)"
                />
                <input type="tel" value={pePhone}
                  onChange={(e) => setPePhone(e.target.value)}
                  className={inputClass} placeholder="Phone (office)" />
                <p className="text-[11px] text-mono-500 italic">
                  Your name, email, and license number are pulled from
                  your user profile. Update them in your account settings
                  if they need correcting.
                </p>
              </div>
            )}
          </div>

          {/* ── Narratives ───────────────────────────────────────── */}
          <div className="space-y-2">
            <SectionHeader
              open={showNarratives} setOpen={setShowNarratives}
              label="Narrative blocks (items 15, 17a, 17b, 19, 44)"
              hint="Optional — auto-defaults are used if left blank"
            />
            {showNarratives && (
              <div className="space-y-3 px-1 pt-2">
                <div>
                  <label className={labelClass}>
                    Item 15 — additional info (if required by platting authority)
                  </label>
                  <textarea rows={2} value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Item 17a — types of non-residential water use
                  </label>
                  <textarea rows={2} value={nonResidentialUseTypes}
                    onChange={(e) => setNonResidentialUseTypes(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Two retail pads at the entrance"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Item 17b — per-type water demand breakdown
                  </label>
                  <textarea rows={2} value={nonResidentialDemandList}
                    onChange={(e) => setNonResidentialDemandList(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Retail: 8 AF/yr; Common irrigation: 2 AF/yr"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Item 19 — sources of demand-estimate info
                  </label>
                  <textarea rows={2} value={demandSources}
                    onChange={(e) => setDemandSources(e.target.value)}
                    className={inputClass}
                    placeholder="Leave blank to use the TWDB-methodology default"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Item 44 — assumptions / uncertainties
                  </label>
                  <textarea rows={3} value={assumptions}
                    onChange={(e) => setAssumptions(e.target.value)}
                    className={inputClass}
                    placeholder="What's outside the scope of this determination?"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-mono-800 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-mono-300 hover:text-mono-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 text-sm font-medium border border-cyan-500/30 disabled:opacity-50"
            >
              {submitting ? 'Working…' : 'Create + Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TceqNewProjectModal;
